import ApplicationError from 'es/app/business-logic/ApplicationError';
import Site from 'es/app/business-logic/domain-models/Site';
import ApolloProvider from 'es/core/net/ApolloProvider';
import { IAccessGroup, IAccessGroupWithAccessControls } from 'es/app/business-logic/domain-models/AccessGroup';
import Guid from 'es/core/types/Guid';
import { FETCH_ACCESS_GROUPS_COMPANY } from './graphql/company.fetchAccessGroupsCompany';
import { FETCH_ACCESS_GROUPS_FACILITY } from './graphql/facility.fetchAccessGroupsFacility';
import { FETCH_ACCESS_GROUPS_GLOBAL } from './graphql/system.fetchAccessGroupsGlobal';
import { FETCH_ALL_ACCESS_GROUPS } from './graphql/system.fetchAllAccessGroups';
import { FETCH_ACCESS_GROUP_DETAILS } from './graphql/system.fetchAccessGroupDetails';
import {
    FetchAccessGroupDetailsQuery,
    FetchAccessGroupDetailsQueryVariables,
    FetchAccessGroupsCompanyQuery,
    FetchAccessGroupsCompanyQueryVariables,
    FetchAccessGroupsFacilityQuery,
    FetchAccessGroupsFacilityQueryVariables,
    FetchAccessGroupsGlobalQuery,
    FetchAccessGroupsGlobalQueryVariables,
    FetchAllAccessGroupsQuery,
    FetchAllAccessGroupsQueryVariables,
} from 'es/app/__generated__/global';

type GraphAccessGroup =
    | FetchAccessGroupsGlobalQuery['system']['accessGroups'][number]
    | NonNullable<FetchAccessGroupsCompanyQuery['company']>['accessGroups'][number]
    | NonNullable<FetchAccessGroupsFacilityQuery['facility']>['accessGroups'][number]
    | FetchAccessGroupDetailsQuery['system']['accessGroups'][number];

export async function fetchAccessGroupsGlobal(): Promise<IAccessGroup[]> {
    const { data } = await ApolloProvider.system.query<
        FetchAccessGroupsGlobalQuery,
        FetchAccessGroupsGlobalQueryVariables
    >({
        query: FETCH_ACCESS_GROUPS_GLOBAL,
    });

    const accessGroups = data?.system.accessGroups.map(convertToAccessGroup);
    if (!accessGroups || !accessGroups.length) return [];

    return accessGroups;
}

export async function fetchAccessGroupsForCompany(companyAlias: string): Promise<IAccessGroup[]> {
    const { data } = await ApolloProvider.company(companyAlias).query<
        FetchAccessGroupsCompanyQuery,
        FetchAccessGroupsCompanyQueryVariables
    >({
        query: FETCH_ACCESS_GROUPS_COMPANY,
    });

    const accessGroups = data?.company?.accessGroups.map(convertToAccessGroup);
    if (!accessGroups || !accessGroups.length) return [];

    return accessGroups;
}

export async function fetchAccessGroupsForFacility(
    companyAlias: string,
    facilityAlias: string
): Promise<IAccessGroup[]> {
    const { data } = await ApolloProvider.facility(companyAlias, facilityAlias).query<
        FetchAccessGroupsFacilityQuery,
        FetchAccessGroupsFacilityQueryVariables
    >({
        query: FETCH_ACCESS_GROUPS_FACILITY,
    });

    const accessGroups = data.facility?.accessGroups.map(convertToAccessGroup);
    if (!accessGroups || !accessGroups.length) return [];

    return accessGroups;
}

export async function fetchAllAccessGroups(): Promise<IAccessGroup[]> {
    const { data } = await ApolloProvider.system.query<FetchAllAccessGroupsQuery, FetchAllAccessGroupsQueryVariables>({
        query: FETCH_ALL_ACCESS_GROUPS,
    });

    if (!data) {
        console.error('No access groups');
        return [];
    }

    const accessGroups = combineAccessGroups(data);

    return [...accessGroups.values()].sort((left, right) => {
        return left.owner === right.owner ? left.name.localeCompare(right.name) : left.owner?.compareTo(right.owner);
    });
}

function combineAccessGroups({ system: { accessGroups: systemAccessGroups, companies } }: FetchAllAccessGroupsQuery) {
    const accessGroups = new Map<Guid, IAccessGroup>();

    systemAccessGroups.forEach(ag => {
        const accessGroup = convertToAccessGroup(ag);
        if (!accessGroups.has(accessGroup.id)) {
            accessGroups.set(accessGroup.id, accessGroup);
        }
    });

    companies.forEach(({ accessGroups: companyAccessGroups, facilities }) => {
        companyAccessGroups.forEach(ag => {
            const accessGroup = convertToAccessGroup(ag);
            if (!accessGroups.has(accessGroup.id)) {
                accessGroups.set(accessGroup.id, accessGroup);
            }
        });
        facilities.forEach(facility => {
            facility.accessGroups.forEach(ag => {
                const accessGroup = convertToAccessGroup(ag);
                if (!accessGroups.has(accessGroup.id)) {
                    accessGroups.set(accessGroup.id, accessGroup);
                }
            });
        });
    });

    return accessGroups;
}

export async function fetchAccessGroupWithAccessControls(id: Guid): Promise<IAccessGroupWithAccessControls | null> {
    const accessGroupFilter = { id_in: [id] };

    const { data } = await ApolloProvider.system.query<
        FetchAccessGroupDetailsQuery,
        FetchAccessGroupDetailsQueryVariables
    >({
        query: FETCH_ACCESS_GROUP_DETAILS,
        variables: {
            accessGroupFilter,
        },
    });

    const accessGroups = data.system.accessGroups.map(convertToAccessGroupWithAccessControls);
    if (accessGroups.length === 1) return accessGroups[0] ?? null;

    ApplicationError.raiseError(
        'The access group could not be loaded.',
        `Expected 1 matching access group for id ${id}; found ${accessGroups.length}.`
    );

    return null;
}

function convertToAccessGroup(accessGroup: GraphAccessGroup): IAccessGroup {
    const { owner, parent, ...rest } = accessGroup;
    const site = Site.get(owner.companyAlias, owner.facilityAlias);

    return {
        ...rest,
        owner: site,
        parentId: parent?.id,
        description: '',
    };
}

function convertToAccessGroupWithAccessControls(accessGroup: GraphAccessGroup): IAccessGroupWithAccessControls {
    return {
        ...convertToAccessGroup(accessGroup),
        accessControls: 'accessControls' in accessGroup ? accessGroup.accessControls : [],
    };
}
